// import { IHighlight } from "@/components/pdf/types";
// import { Note } from "@/stores/slices/noteSlice";

import { uuid, uuidby } from "@/lib/uuid";
import { decodeHtmlEntities } from "@/lib/html-entities";
import { IHighlight, Scaled } from "@/components/pdf/types";
import { Note } from "@/stores/context/createNoteStore";
import { Anchors, Coordinate, Coordinates } from "@/stores/slices/chatSlice";
import sanitizeHtml from "sanitize-html";
import { type PDFViewer } from "pdfjs-dist/web/pdf_viewer";
import rehypeStringify from 'rehype-stringify'
import remarkFrontmatter from 'remark-frontmatter'
import remarkGfm from 'remark-gfm'
import remarkParse from 'remark-parse'
import remarkRehype from 'remark-rehype'
import { unified } from 'unified'

// type Obj = {
//   [key in string]: any;
// };
// type JSONArray = JSONData[];

// export type DeepCamelToSnake<T, T_MF> = T extends Array<infer Item>
//   ? Array<DeepCamelToSnake<Item, T_MF>> // 处理数组元素
//   : T extends object
//   ? RecordCamelToSnake<{ [K in keyof T]: DeepCamelToSnake<T[K]> }> // 处理对象属性
//   : T; // 基本类型直接返回

// type Modifier<T> = (value: T, key: string | number) => [typeof value, string];
// type Filter<T> = (value: T, key: string | number) => boolean;
// type Transfer<T> = {
//   filter?: Filter<T>,
//   modifier?: Modifier<T>,
// };
// const default_filter: JSONFilter = () => true;
// const default_modifier: JSONModifier = (value, key) => ([value, key]);

// export function transfer(data, {
//   filter = default_filter,
//   modifier = default_modifier,
// }) {
//   function iterate(data) {
//     if (Array.isArray(data)) {
//       return data
//         .filter((item, index) => filter(item, index))
//         .map((item) => iterate(item));
//     } else if (typeof data === 'object' && data !== null) {
//       const newObj = {};
//       for (const key in data) {
//         if (filter(data[key], key)) {
//           const [newVal, newKey] = modifier(data[key], key);
//           newObj[newKey] = newVal;
//         }
//       }
//       return newObj;
//     }
//     return modifier(data, '')[0];
//   }
//   return iterate(data);
// }

export function notesToHighlights(notes: Note[]): IHighlight[] {
  return notes
    .filter((note) => note.type === 'quote' && note.viewer?.color && note.viewer?.position)
    .map(({ id, viewer, ...rest }) => {
      return ({
        ...rest,
        id,
        color: viewer.color!,
        position: viewer.position!,
      });
    });
}

export function numberToLetter(number: number): string {
  if (number < 1 || number > 26) {
    // Invalid input. Please enter a number between 1 and 26.
    return '';
  }

  const charCodeA = 'a'.charCodeAt(0);
  const letter = String.fromCharCode(charCodeA + number - 1);

  return letter;
}

/**
 * 将坐标数组转换为锚点数组
 * 
 * @param coordinates - 坐标数据
 * @param viewer - PDF 查看器实例（可选），用于动态获取页面尺寸
 * @returns 返回锚点数组
 * 
 * @description
 * 由于后端改变了解析方式，无法再输出 pageSize 信息。
 * 前端使用 pdf.js 的 page.getViewport({ scale: 1 }) 动态获取页面尺寸。
 * 如果提供了 viewer，从 PDF 文档中获取准确的页面尺寸；否则使用默认 A4 尺寸（612x792，72 DPI）。
 */
export async function coordinatesToAnchors(
  coordinates: Coordinates,
  viewer?: PDFViewer
): Promise<Anchors> {
  const anchors: Anchors = [];
  const coordinateArrays = Object.values(coordinates);

  for (const rects of coordinateArrays) {
    if (!rects || rects.length === 0) continue;

    const page = (+rects[0].pageNumber + 1).toString();

    for (let index = 0; index < rects.length; index++) {
      const { pageNumber, points } = rects[index];
      // points 是相对坐标（0-1 范围），需要转换为绝对坐标
      const [x1Relative, y1Relative, x2Relative, y2Relative] = points;

      let width: number;
      let height: number;

      // 使用 pdf.js 从 viewer 中获取原始页面尺寸
      // page.getViewport({ scale: 1 }) 返回 PDF 页面的原始尺寸（72 DPI）
      if (viewer?.pdfDocument) {
        try {
          // 检查文档是否仍然有效
          const numPages = viewer.pdfDocument.numPages;
          if (pageNumber >= 0 && pageNumber < numPages) {
            const page = await viewer.pdfDocument.getPage(pageNumber);
            const viewport = page.getViewport({ scale: 1 });
            width = viewport.width;
            height = viewport.height;
          } else {
            // 页面编号无效，使用默认 A4 尺寸
            width = 612;
            height = 792;
          }
        } catch (error: any) {
          // 捕获 RenderingCancelledException 和其他错误，静默处理
          // 当 PDF 文档被销毁或页面渲染被取消时，使用默认尺寸
          if (error?.name === 'RenderingCancelledException' || error?.name === 'InvalidPDFException') {
            // 静默处理，不输出错误日志
          } else {
            console.error('Failed to get page size from viewer:', error);
          }
          // 如果获取失败，使用默认 A4 尺寸（72 DPI）
          width = 612;
          height = 792;
        }
      }
      // 如果没有 viewer（处理历史数据或离线场景），使用默认 A4 尺寸
      else {
        width = 612;
        height = 792;
      }

      // 将相对坐标（0-1）转换为绝对坐标（基于页面尺寸）
      // scaledToViewport 函数期望 Scaled 对象中的 x1, y1, x2, y2 是绝对坐标
      const x1 = x1Relative * width;
      const y1 = y1Relative * height;
      const x2 = x2Relative * width;
      const y2 = y2Relative * height;

      const quote = rects.length > 1 ? numberToLetter(index + 1) : '';
      anchors.push({
        anchorId: page + quote,
        x1, y1, x2, y2,
        width, height,
        pageNumber: pageNumber + 1,
      });
    }
  }

  return anchors;
}

/**
 * 将坐标转换为锚点
 * 
 * @param coordinate - 坐标数据（包含 pageNumber 和 points）
 * @param viewer - PDF 查看器实例（可选），用于动态获取页面尺寸
 * @returns 返回包含绝对坐标和页面尺寸的 Scaled 对象
 * 
 * @description
 * 由于后端改变了解析方式，无法再输出 pageSize 信息。
 * 传递 viewer 参数，使用 pdf.js 动态获取页面尺寸。
 * points 是相对坐标（0-1 范围），需要转换为基于 PDF 原始页面尺寸的绝对坐标。
 * 
 * @example
 * const anchor = await coordinateToAnchor({
 *   pageNumber: 1,
 *   points: [0.1, 0.2, 0.3, 0.4]
 * }, viewer);
 */
export async function coordinateToAnchor(
  coordinate: Coordinate,
  viewer?: PDFViewer
): Promise<Scaled> {
  const { pageNumber, points } = coordinate;
  // points 是相对坐标（0-1 范围）
  const [x1Relative, y1Relative, x2Relative, y2Relative] = points;

  let width: number;
  let height: number;

  // 使用 pdf.js 从 viewer 中获取原始页面尺寸
  // page.getViewport({ scale: 1 }) 返回 PDF 页面的原始尺寸（72 DPI）
  if (viewer?.pdfDocument) {
    try {
      // 检查文档是否仍然有效
      const numPages = viewer.pdfDocument.numPages;
      if (pageNumber >= 0 && pageNumber < numPages) {
        const page = await viewer.pdfDocument.getPage(pageNumber);
        const viewport = page.getViewport({ scale: 1 });
        width = viewport.width;
        height = viewport.height;
      } else {
        // 页面编号无效，使用默认 A4 尺寸
        width = 612;
        height = 792;
      }
    } catch (error: any) {
      // 捕获 RenderingCancelledException 和其他错误，静默处理
      // 当 PDF 文档被销毁或页面渲染被取消时，使用默认尺寸
      if (error?.name === 'RenderingCancelledException' || error?.name === 'InvalidPDFException') {
        // 静默处理，不输出错误日志
      } else {
        console.error('Failed to get page size from viewer:', error);
      }
      // 如果获取失败，使用默认 A4 尺寸（72 DPI）
      width = 612;
      height = 792;
    }
  }
  // 如果没有 viewer（处理历史数据或离线场景），使用默认 A4 尺寸
  else {
    width = 612;
    height = 792;
  }

  // 将相对坐标（0-1）转换为绝对坐标（基于 PDF 原始页面尺寸）
  // scaledToViewport 函数会将基于 scaled.width 的绝对坐标转换为当前视口坐标
  const x1 = x1Relative * width;
  const y1 = y1Relative * height;
  const x2 = x2Relative * width;
  const y2 = y2Relative * height;

  return {
    x1, y1, x2, y2,
    width, height,
    pageNumber: pageNumber + 1,
  };
}

export const DATA_NOTES_ATTR = 'data-findin-notes';

type PartialNote = Pick<Note, 'id' | 'pid' | 'content' | 'children'> & Partial<Note>;
type Entities = Record<string, PartialNote>;
export type UsedNotes = {
  ids: string[];
  entities: Entities;
};

export const parseNotesToHTMLStr = (ids: string[], entities: Entities): string => {
  const result: string[] = [];
  const usedEntities: Entities = {};
  const extractNode = (noteId: string): string => {
    const note = entities[noteId];
    if (!note) return '';

    const contentSpan = `<span>${note.content}</span>`;
    const imageTags = note.images?.map((image) =>
      `<img src="${image.url}" />`).join('') || '';
    const childrenList = note.children.map((childId) =>
      extractNode(childId)).join('');
    usedEntities[uuidby(note.id)] = {
      // 修改id
      id: uuidby(note.id),
      pid: uuidby(note.pid),
      content: note.content,
      children: note.children.map(id => uuidby(id)),
      images: note.images,
    };
    return `<li>${contentSpan}${imageTags}<br/>${childrenList ? `<ul>${childrenList}</ul>` : ''
      }</li>`;
  };

  ids.forEach((id) => {
    const extractedNode = extractNode(id);
    if (extractedNode) {
      result.push(extractedNode);
    }
  });
  const jsonstr = encodeURIComponent(JSON.stringify({
    ids: ids.map(id => uuidby(id)),
    entities: usedEntities,
  }));

  return `<ul ${DATA_NOTES_ATTR}="${jsonstr}">${result.join('')}</ul>`;
};

export async function parseHTMLStrToNotes(html: string, rootId: string, debug: boolean = false): Promise<UsedNotes> {
  const jsonMatch = html.match(new RegExp(`${DATA_NOTES_ATTR}="(.*?)"`));
  if (jsonMatch?.[1]) {
    try {
      const parsed = JSON.parse(decodeURIComponent(jsonMatch[1])) as UsedNotes;
      const { ids, entities } = parsed;
      ids.forEach((id) => {
        entities[id].pid = rootId;
      });
      return {
        ids: parsed.ids,
        entities,
      }
    } catch (error) {
      console.error('Error parsing JSON from pasted content:', error);
    }
  }
  debug && console.log(html);
  html = sanitizeHtml(html, {
    allowedTags: ["b", "i", "em", "strong", "p", "ul", "ol", "li", "div"],
  });
  debug && console.log(html);
  if (!isHTML(html)) {
    html = await markdownToHTML(html);
  }
  html = html.replace(/>\s+</g, '><').trim();
  debug && console.log(html);
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const entities: Entities = {};
  const noteTree = parseChildNode(doc.body as HTMLElement, rootId, entities);
  return {
    ids: noteTree.map((note) => note.id),
    entities,
  };
}

function parseChildNode(element: HTMLElement, parentId: string, entities: Entities): PartialNote[] {
  const notes: PartialNote[] = [];
  const childNodes = element.childNodes;
  let prevInlineNodeIndex = -1;
  let prevIsLNote = false;

  childNodes.forEach((childNode) => {
    const tagName = childNode.nodeName.toLocaleLowerCase();
    if (['ul', 'ol'].includes(tagName)) {
      const childElement = childNode as HTMLElement;
      const note: PartialNote = notes[notes.length - 1];
      if (note && !prevIsLNote) {
        const children = parseChildNode(childElement, note.id, entities).map((note) => note.id);
        note.children = note.children.concat(children);
      } else {
        const sublings = parseChildNode(childElement, parentId, entities);
        notes.push(...sublings);
      }
      prevIsLNote = true;
    } else if (['li', 'p', 'div'].includes(tagName)) {
      const childElement = childNode as HTMLElement;
      const sublings = parseChildNode(childElement, parentId, entities);
      notes.push(...sublings);
      prevIsLNote = false;
    } else {
      const content = getHTMLFragment(childNode);
      if (notes.length - 1 === prevInlineNodeIndex && prevInlineNodeIndex >= 0) {
        const note = notes[notes.length - 1];
        note.content += content;
      } else {
        const note: PartialNote = {
          id: uuid(),
          pid: parentId || '',
          content,
          children: [],
          type: 'comment',
          viewer: {},
          images: [],
        };
        notes.push(note);
        prevInlineNodeIndex += 1;
      }
      prevIsLNote = false;
    }
  });
  notes.forEach((note) => entities[note.id] = note);
  return notes;
}

function getHTMLFragment(node: Node): string {
  if (node.nodeType === Node.ELEMENT_NODE) {
    return (node as Element).outerHTML || '';
  } else if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent || '';
  }
  return '';
}

export async function markdownToHTML(doc: string) {
  const file = await unified()
    .use(remarkParse)
    .use(remarkFrontmatter)
    .use(remarkGfm)
    .use(remarkRehype)
    .use(rehypeStringify)
    .process(doc);
  return String(file).replace(/>\s+</g, '><').trim();
}

function isHTML(text: string) {
  // 使用正则表达式检查是否包含 HTML 标签
  const htmlRegex = /<[a-z][\s\S]*>/i;
  return htmlRegex.test(text);
}

/**
 * 将 CSL 格式的 HTML 转换为纯文本
 * @param html - CSL 格式的 HTML 字符串（如 <div class="csl-entry">...</div>）
 * @returns 合并后的文本字符串
 * @example
 * htmlToCslText('<div class="csl-entry"><div class="csl-left-margin">1.</div><div class="csl-right-inline">Text</div></div>')
 * // 返回: "1. Text"
 */
export function htmlToCslText(html: string): string {
  return decodeHtmlEntities(html
    .replace(/<div[^>]*class="[^"]*csl-left-margin[^"]*"[^>]*>(.*?)<\/div>/g, '$1 ') // 提取 csl-left-margin 内容并加上空格
    .replace(/<i>(.*?)<\/i>/g, '*$1*')    // <i> 转 *
    .replace(/<b>(.*?)<\/b>/g, '**$1**')  // <b> 转 **
    .replace(/<em>(.*?)<\/em>/g, '*$1*') // <em> 转 *
    .replace(/<[^>]*>/g, '').trim());
}
